// src/disc/DiscComparisonSetup.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Paper,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Checkbox,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

const TestCard = styled(Card)(({ theme, selected }) => ({
  marginBottom: theme.spacing(2),
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  backgroundColor: selected ? theme.palette.primary.light + '10' : 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.light + '20' : '#f5f5f5',
  },
}));

export default function DiscComparisonSetup({
  analysisType,
  positions,
  targetPosition,
  discTests,
  selectedTests,
  bossTestId,
  teamContext,
  loading,
  onTargetPositionChange,
  onTestSelection,
  onBossSelection,
  onTeamContextChange,
  onStartAnalysis,
  onBackToTypeSelection,
  onCompetenciesChange,
  canStartAnalysis,
  formatDate,
}) {
  const { t } = useTranslation();
  const { authToken, language, showAlert } = useStore.getState();
  const [testFilter, setTestFilter] = React.useState('');

  // Estados para competencias
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);

  // Cargar capabilities al montar el componente
  useEffect(() => {
    loadCapabilities();
  }, [language]);

  // Función para cargar capabilities
  const loadCapabilities = async () => {
    try {
      setLoadingCapabilities(true);
      const response = await api.get(
        `/capabilities?language=${language}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response && response.data.response.status_response === 'OK') {
        setCapabilities(response.data.response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar competencias:', error);
      showAlert('Error al cargar competencias', 'error');
    } finally {
      setLoadingCapabilities(false);
    }
  };

  // Manejar cambio de competencias seleccionadas
  const handleCompetenciesChange = (event, newValue) => {
    setSelectedCompetencies(newValue);
    if (onCompetenciesChange) {
      onCompetenciesChange(newValue);
    }
  };

  const filteredTests = discTests.filter(test => {
    const searchTerm = testFilter.toLowerCase();
    const applicantName = (test.applicantName || '').toLowerCase();
    const positionName = (test.positionName || '').toLowerCase();
    return applicantName.includes(searchTerm) || positionName.includes(searchTerm);
  });

  const selectedTestsCount = Object.keys(selectedTests).length;

  const getDiscProfile = (test) => {
    const natural = test.discResults?.finalScores?.profile?.natural || {};
    return `D:${natural.D || 0}% I:${natural.I || 0}% S:${natural.S || 0}% C:${natural.C || 0}%`;
  };

  const renderTeamContext = () => (
    <StyledPaper sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        📋 {t('disc.discComparisons.teamContext')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('disc.discComparisons.teamContextRequired')}
      </Typography>

      <TextField
        fullWidth
        required
        label={t('disc.discComparisons.teamName')}
        placeholder={t('disc.discComparisons.teamNamePlaceholder')}
        value={teamContext.name}
        onChange={(e) => onTeamContextChange({ ...teamContext, name: e.target.value })}
        error={teamContext.name.trim() === ''}
        helperText={teamContext.name.trim() === '' ? t('disc.discComparisons.teamNameRequired') : ''}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        required
        multiline
        rows={3}
        label={t('disc.discComparisons.teamDescription')}
        placeholder={t('disc.discComparisons.teamDescriptionPlaceholder')}
        value={teamContext.description}
        onChange={(e) => onTeamContextChange({ ...teamContext, description: e.target.value })}
        error={teamContext.description.trim() === ''}
        helperText={teamContext.description.trim() === '' ? t('disc.discComparisons.teamDescriptionRequired') : ''}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        required
        multiline
        rows={2}
        label={t('disc.discComparisons.teamObjectives')}
        placeholder={t('disc.discComparisons.teamObjectivesPlaceholder')}
        value={teamContext.objectives}
        onChange={(e) => onTeamContextChange({ ...teamContext, objectives: e.target.value })}
        error={teamContext.objectives.trim() === ''}
        helperText={teamContext.objectives.trim() === '' ? t('disc.discComparisons.teamObjectivesRequired') : ''}
      />
    </StyledPaper>
  );

  const renderPositionSelector = () => (
    <StyledPaper sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        🎯 {t('disc.discComparisons.selectPosition')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('disc.discComparisons.positionRequired')}
      </Typography>

      <Autocomplete
        value={targetPosition}
        onChange={(event, newValue) => onTargetPositionChange(newValue)}
        options={positions}
        getOptionLabel={(option) => option.name || ''}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {option.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {option.industria}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('positions.searchPlaceholder')}
            placeholder={t('positions.searchPlaceholder')}
            fullWidth
            required
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading.positions && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        loading={loading.positions}
        noOptionsText={t('disc.discComparisons.noPositionsFound')}
      />

      {targetPosition && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            {t('disc.discComparisons.selectPosition')}:
          </Typography>
          <Typography variant="body2">
            <strong>{targetPosition.name}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {targetPosition.industria}
          </Typography>
        </Box>
      )}
    </StyledPaper>
  );

  const renderCompetenciesSelector = () => (
    <StyledPaper sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          {t('disc.discComparisons.competenciesTitle')} <Typography component="span" variant="caption" color="text.secondary">{t('disc.discComparisons.competenciesOptional')}</Typography>
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('disc.discComparisons.competenciesDescription')}
      </Typography>

      <Autocomplete
        multiple
        disableCloseOnSelect // ✅ AGREGADO: Mantiene abierto el dropdown
        value={selectedCompetencies}
        onChange={handleCompetenciesChange}
        options={capabilities}
        getOptionLabel={(option) => option.name || option.capabilityName || ''}
        loading={loadingCapabilities}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('disc.discComparisons.selectCompetencies')}
            placeholder={t('disc.discComparisons.searchCompetencies')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCapabilities && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.name || option.capabilityName}
              {...getTagProps({ index })}
              color="primary"
              size="small"
            />
          ))
        }
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<CheckCircleIcon />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2">
                {option.name || option.capabilityName}
              </Typography>
              {option.description && (
                <Typography variant="caption" color="text.secondary">
                  {option.description.substring(0, 80)}
                  {option.description.length > 80 && '...'}
                </Typography>
              )}
            </Box>
          </li>
        )}
        noOptionsText={t('disc.discComparisons.noCompetenciesFound')}
      />

      {selectedCompetencies.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            {selectedCompetencies.length} {t('disc.discComparisons.competenciesSelected')}
          </Typography>
        </Alert>
      )}
    </StyledPaper>
  );

  const renderTestsList = () => {
    if (loading.tests) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('disc.discComparisons.loadingTests')}
          </Typography>
        </Box>
      );
    }

    if ((analysisType === 'compareCandidates' || analysisType === 'candidateVsBoss') && !targetPosition) {
      return null;
    }

    if (filteredTests.length === 0) {
      return (
        <Alert severity="info">
          {testFilter ? t('noResults') : t('disc.discComparisons.noDiscTestsFound')}
        </Alert>
      );
    }

    return (
      <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
        {filteredTests.map((test) => {
          const isSelected = !!selectedTests[test.id]; // ✅ CAMBIO: Convertir explícitamente a booleano
          const isBoss = bossTestId === test.id;

          return (
            <TestCard
              key={test.id}
              selected={isSelected}
              onClick={(e) => {
                // ✅ CAMBIO: Solo ejecutar si NO se hace clic en el checkbox o botón
                if (
                  e.target.tagName !== 'INPUT' &&
                  !e.target.closest('button') &&
                  !e.target.closest('.MuiCheckbox-root')
                ) {
                  onTestSelection(test.id);
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Checkbox
                    checked={isSelected} // ✅ Ahora isSelected es explícitamente booleano
                    onChange={(e) => {
                      e.stopPropagation();
                      onTestSelection(test.id);
                    }}
                    onClick={(e) => e.stopPropagation()} // ✅ AGREGADO: También detener onClick
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    color="primary"
                  />

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {test.applicantName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {test.positionName || t('disc.discComparisons.noPosition')}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {t('disc.discComparisons.testCompleted')}: {formatDate(test.completedAt)}
                    </Typography>

                    <Chip
                      label={getDiscProfile(test)}
                      size="small"
                      sx={{ mt: 1 }}
                      variant="outlined"
                    />
                  </Box>

                  {analysisType === 'candidateVsBoss' && isSelected && (
                    <Button
                      size="small"
                      variant={isBoss ? 'contained' : 'outlined'}
                      color="warning"
                      startIcon={<StarIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onBossSelection(test.id);
                      }}
                    >
                      {isBoss ? t('disc.discComparisons.bossLabel') : t('disc.discComparisons.markAsBoss')}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </TestCard>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackToTypeSelection}
          variant="outlined"
        >
          {t('goBack')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          {analysisType === 'teamAnalysis' ? renderTeamContext() : renderPositionSelector()}

          {/* Selector de competencias (solo para compareCandidates y candidateVsBoss) */}
          {(analysisType === 'compareCandidates' || analysisType === 'candidateVsBoss') &&
            renderCompetenciesSelector()
          }

          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              📊 {t('summary')}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('disc.discComparisons.analysisType')}:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {analysisType === 'compareCandidates' && t('disc.discComparisons.compareCandidates')}
                {analysisType === 'candidateVsBoss' && t('disc.discComparisons.candidateVsBoss')}
                {analysisType === 'teamAnalysis' && t('disc.discComparisons.teamAnalysis')}
              </Typography>
            </Box>

            {targetPosition && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('position')}:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {targetPosition.name}
                </Typography>
              </Box>
            )}

            {selectedCompetencies.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('disc.discComparisons.competenciesLabel')}d
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedCompetencies.map((comp, index) => (
                    <Chip
                      key={index}
                      label={comp.name || comp.capabilityName}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {analysisType === 'teamAnalysis' && teamContext.name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('disc.discComparisons.teamName')}:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {teamContext.name}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('disc.discComparisons.selectedTests')}:
              </Typography>
              <Typography variant="h4" color="primary">
                {selectedTestsCount}
              </Typography>
            </Box>

            {analysisType === 'candidateVsBoss' && bossTestId && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('disc.discComparisons.boss')}:
                </Typography>
                <Chip
                  icon={<StarIcon />}
                  label={discTests.find(t => t.id === bossTestId)?.applicantName || 'N/A'}
                  color="warning"
                  size="small"
                />
              </Box>
            )}
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={5}>
          <StyledPaper>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                🧬 {t('disc.discComparisons.discTestsAvailable')} ({filteredTests.length})
              </Typography>
              {loading.tests && <CircularProgress size={20} />}
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder={t('disc.discComparisons.searchApplicant')}
              value={testFilter}
              onChange={(e) => setTestFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Aviso sobre tests evaluados */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {t('disc.evaluatedTestsNotice')}
              </Typography>
            </Alert>

            {renderTestsList()}
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={3}>
          <StyledPaper sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              ✅ {t('disc.discComparisons.requirements.title')}
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    analysisType === 'teamAnalysis'
                      ? t('disc.discComparisons.requirements.fillTeamContext')
                      : t('disc.discComparisons.requirements.selectPosition')
                  }
                  secondary={
                    analysisType === 'teamAnalysis'
                      ? (teamContext.name && teamContext.description && teamContext.objectives ? '✓' : '✗')
                      : (targetPosition ? '✓' : '✗')
                  }
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: (analysisType === 'teamAnalysis'
                      ? (teamContext.name && teamContext.description && teamContext.objectives)
                      : targetPosition) ? 'text.primary' : 'text.secondary',
                  }}
                />
              </ListItem>

              <ListItem>
                <ListItemText
                  primary={t('disc.discComparisons.requirements.selectMinTests')}
                  secondary={selectedTestsCount >= 2 ? '✓' : `✗ (${selectedTestsCount}/2)`}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: selectedTestsCount >= 2 ? 'text.primary' : 'text.secondary',
                  }}
                />
              </ListItem>

              {analysisType === 'candidateVsBoss' && (
                <ListItem>
                  <ListItemText
                    primary={t('disc.discComparisons.requirements.selectBoss')}
                    secondary={bossTestId ? '✓' : '✗'}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: bossTestId ? 'text.primary' : 'text.secondary',
                    }}
                  />
                </ListItem>
              )}
            </List>
          </StyledPaper>

          <StyledPaper>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!canStartAnalysis() || loading.analysis}
              onClick={onStartAnalysis}
              sx={{ mb: 2 }}
            >
              {loading.analysis ? t('disc.discComparisons.starting') : t('disc.discComparisons.startAnalysis')}
            </Button>

            {!canStartAnalysis() && (
              <Alert severity="info" size="small">
                <Typography variant="caption">
                  {t('completeRequiredFields')}
                </Typography>
              </Alert>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </Box>
  );
}